import { GetUrlToOpenUsingDefault } from 'flora-common';
import {
    ActivityFeed as LazyActivityFeed,
    ActivityFeedTitle as LazyActivityFeedTitle,
} from 'owa-activity-feed';
import { logUsage } from 'owa-analytics';
import { DrawerPane } from 'owa-drawer';
import type { MailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { observer } from 'owa-mobx-react';
import { popoutMailReadingPane } from 'owa-popout';
import type { OwaHeaderPaneProps } from '../../types/OwaHeaderPaneProps';
import { isImmutableId } from 'owa-immutable-id-store';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { ConvertIdSource, translateRestIdsToRestImmutableIds } from 'owa-immutable-id';
import { launchCalendarReadingPane } from 'owa-calendar-forms-launch-handlers';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import type { FunctionComponent } from 'react';

interface OpenMailAction {
    openMail: (mailboxInfo: MailboxInfo, messageId: string, itemId?: string) => void;
}

interface OpenLinkAction {
    openLink: (mailboxInfo: MailboxInfo, url: string) => void;
}

interface OpenCalendarAction {
    openCalendar: (mailboxInfo: MailboxInfo, itemId: string) => void;
}

const notificationsOpenMailAction: OpenMailAction = {
    openMail: (mailboxInfo, messageId, itemId) => {
        popoutMailReadingPane(
            /* itemId */ itemId,
            /* conversationId */ undefined,
            /* internetMessageId */ messageId,
            /* mailboxInfo */ mailboxInfo
        );
    },
};

const notificationsOpenLinkAction: OpenLinkAction = {
    openLink: async (mailboxInfo, url) => {
        /* eslint-disable-next-line owa-custom-rules/require-undefined-parameter -- (https://aka.ms/OWALintWiki)
         * Flight checks that supply MailboxInfo should be defined as AccountFeatureName value and should be checked using isAccountFeatureEnabled to ensure consistent checking.
         *	> The parameter mailboxInfo must be undefined. Feature flight: 'doc-flora-stage1' */
        const isFloraEnabled = isFeatureEnabled('doc-flora-stage1', mailboxInfo);
        isFloraEnabled ? await openLinkWithFLORA(url) : window.open(url, '_blank');
    },
};

const notificationsOpenCalendarAction: OpenCalendarAction = {
    openCalendar: (mailboxInfo, itemId) => {
        if (isImmutableId(itemId)) {
            const ewsImmutableId = convertRestIdToEwsId(itemId);
            launchCalendarReadingPane({
                itemId: { Id: ewsImmutableId, mailboxInfo },
                entrySource: 'ActivityFeed' as ReadingPaneEntrySourceType,
            });
        } else {
            // The itemId needs to be converted to the immutable id before opening the calendar form.
            translateRestIdsToRestImmutableIds([itemId], ConvertIdSource.ActivityFeed).then(
                response => {
                    const id = response[0];
                    if (id) {
                        launchCalendarReadingPane({
                            itemId: { Id: id, mailboxInfo },
                            entrySource: 'ActivityFeed' as ReadingPaneEntrySourceType,
                        });
                    }
                }
            );
        }
    },
};

async function openLinkWithFLORA(url: string): Promise<void> {
    const urlToOpen = await GetUrlToOpenUsingDefault(url);

    if (urlToOpen) {
        // OW: 270495 Need to use the main window instead of targetWindow to prevent a dialog from showing in popout's.
        const handle = window.open(urlToOpen, '_self');
        if (!handle) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logUsage('FLORAFailedToOpen', undefined);
        }
    } else {
        window.open(url, '_blank');
    }
}

const notificationsPaneTitle = () => {
    return <LazyActivityFeedTitle />;
};

const defaultExport: FunctionComponent<OwaHeaderPaneProps> = observer(function NotificationsPane(
    props: OwaHeaderPaneProps
) {
    return (
        <DrawerPane
            paneId={props.paneId}
            headerText={notificationsPaneTitle()}
            role={'complementary'}
        >
            <LazyActivityFeed
                openMailAction={notificationsOpenMailAction}
                openLinkAction={notificationsOpenLinkAction}
                openCalendarAction={notificationsOpenCalendarAction}
            />
        </DrawerPane>
    );
},
'NotificationsPane');
export default defaultExport;
